import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";
import type { OpencodeToolDisplay } from "../shared";

import {
  mediaMcpGenerateImageInputSchema,
  mediaMcpGenerateVideoInputSchema,
  mediaMcpGetGenerationStatusInputSchema,
} from "schemas/agentTools/replo/mediaMcp";
import { z } from "zod";

import { getToolDisplayText, normalizeIdentifier, quotedText } from "../shared";
import { getToolPartOutputJson } from "./bedrock-shared";

type MediaGenerationToolName =
  | "bedrock_generate_image"
  | "bedrock_generate_video";
type MediaKind = "image" | "mixed" | "video";

const mediaMcpGenerateJobLookupOutputSchema = z.object({
  jobId: z.string().optional(),
  jobIds: z.array(z.string()).optional(),
  results: z.array(z.object({ jobId: z.string() })).optional(),
});

const mediaMcpGenerationStatusOutputSchema = z.object({
  results: z.array(z.object({ kind: z.enum(["image", "video"]).optional() })),
});

function getMediaGenerationToolName({
  toolName,
}: {
  toolName: string;
}): MediaGenerationToolName | null {
  const normalizedToolName = normalizeIdentifier({ value: toolName });
  if (normalizedToolName.endsWith("generate_image")) {
    return "bedrock_generate_image";
  }

  if (normalizedToolName.endsWith("generate_video")) {
    return "bedrock_generate_video";
  }

  return null;
}

function getGenerationJobIds({ toolPart }: { toolPart: OpencodeToolPart }) {
  const parsedOutput = mediaMcpGenerateJobLookupOutputSchema.safeParse(
    getToolPartOutputJson({ toolPart }),
  );
  if (!parsedOutput.success) {
    return [];
  }

  const singleJobId = parsedOutput.data.jobId?.trim();
  if (singleJobId) {
    return [singleJobId];
  }

  return (
    parsedOutput.data.jobIds ??
    parsedOutput.data.results?.map((result) => result.jobId) ??
    []
  ).filter((jobId) => jobId.trim().length > 0);
}

function getGenerationStatusKind({ toolPart }: { toolPart: OpencodeToolPart }) {
  const parsedOutput = mediaMcpGenerationStatusOutputSchema.safeParse(
    getToolPartOutputJson({ toolPart }),
  );
  if (!parsedOutput.success) {
    return null;
  }

  const kinds = new Set<MediaKind>();
  for (const result of parsedOutput.data.results) {
    if (result.kind) {
      kinds.add(result.kind);
    }
  }

  if (kinds.size === 0) {
    return null;
  }

  return kinds.size === 1 ? kinds.values().next().value ?? null : "mixed";
}

function getImagePromptDetails({ toolPart }: { toolPart: OpencodeToolPart }) {
  const parsedInput = mediaMcpGenerateImageInputSchema.safeParse(
    toolPart.state.input,
  );
  if (!parsedInput.success) {
    return [];
  }

  const prompt = parsedInput.data.prompt?.trim();
  if (prompt) {
    return [quotedText({ value: prompt })];
  }

  return (
    parsedInput.data.requests?.map((request) => {
      return quotedText({ value: request.prompt });
    }) ?? []
  );
}

function getVideoPromptDetails({ toolPart }: { toolPart: OpencodeToolPart }) {
  const parsedInput = mediaMcpGenerateVideoInputSchema.safeParse(
    toolPart.state.input,
  );
  return parsedInput.success
    ? [quotedText({ value: parsedInput.data.prompt })]
    : [];
}

function getMediaPromptDetails({ toolPart }: { toolPart: OpencodeToolPart }) {
  const mediaGenerationToolName = getMediaGenerationToolName({
    toolName: toolPart.tool,
  });
  if (!mediaGenerationToolName) {
    return [];
  }

  return mediaGenerationToolName === "bedrock_generate_image"
    ? getImagePromptDetails({ toolPart })
    : getVideoPromptDetails({ toolPart });
}

function findGenerationJobDisplay({
  jobIds,
  toolParts,
}: {
  jobIds: readonly string[];
  toolParts: readonly OpencodeToolPart[];
}) {
  const requestedJobIds = new Set(jobIds);
  const matchingKinds = new Set<MediaKind>();
  const promptDetails: string[] = [];

  for (const sourceToolPart of toolParts) {
    const mediaGenerationToolName = getMediaGenerationToolName({
      toolName: sourceToolPart.tool,
    });
    if (!mediaGenerationToolName) {
      continue;
    }

    const generatedJobIds = getGenerationJobIds({ toolPart: sourceToolPart });
    const generatedPromptDetails = getMediaPromptDetails({
      toolPart: sourceToolPart,
    });
    for (const [index, generatedJobId] of generatedJobIds.entries()) {
      if (!requestedJobIds.has(generatedJobId)) {
        continue;
      }

      matchingKinds.add(
        mediaGenerationToolName === "bedrock_generate_image"
          ? "image"
          : "video",
      );
      const promptDetail =
        generatedPromptDetails[index] ?? generatedPromptDetails[0] ?? null;
      if (promptDetail) {
        promptDetails.push(promptDetail);
      }
    }
  }

  if (promptDetails.length === 0 && matchingKinds.size === 0) {
    return null;
  }

  return {
    kind:
      matchingKinds.size === 1
        ? matchingKinds.values().next().value ?? "mixed"
        : "mixed",
    promptDetails,
  };
}

export function getMediaGenerationDisplay({
  kind,
  toolPart,
}: {
  kind: "image" | "video";
  toolPart: OpencodeToolPart;
}): OpencodeToolDisplay {
  const promptDetails =
    kind === "image"
      ? getImagePromptDetails({ toolPart })
      : getVideoPromptDetails({ toolPart });
  const promptDetail = promptDetails.join(", ");
  const mediaKindText = promptDetails.length === 1 ? kind : `${kind}s`;

  return {
    summary: getToolDisplayText({
      completed:
        promptDetail.length > 0
          ? `Generated ${mediaKindText} ${promptDetail}`
          : `Generated ${kind}`,
      pending:
        promptDetail.length > 0
          ? `Generating ${mediaKindText} ${promptDetail}`
          : `Generating ${kind}`,
      running:
        promptDetail.length > 0
          ? `Generating ${mediaKindText} ${promptDetail}`
          : `Generating ${kind}`,
    }),
    detail: null,
  };
}

export function getMediaGenerationStatusDisplay({
  toolPart,
  toolParts,
}: {
  toolPart: OpencodeToolPart;
  toolParts: readonly OpencodeToolPart[];
}): OpencodeToolDisplay {
  const parsedInput = mediaMcpGetGenerationStatusInputSchema.safeParse(
    toolPart.state.input,
  );
  const requestedJobIds = parsedInput.success ? parsedInput.data.jobIds : [];
  const generationJobDisplay = findGenerationJobDisplay({
    jobIds: requestedJobIds,
    toolParts,
  });
  const mediaKind =
    generationJobDisplay?.kind ??
    getGenerationStatusKind({ toolPart }) ??
    "media";
  const mediaKindText = mediaKind === "mixed" ? "mixed media" : mediaKind;
  const promptCount = generationJobDisplay?.promptDetails.length ?? 0;
  const statusItemCount =
    promptCount > 0 ? promptCount : Math.max(requestedJobIds.length, 1);
  const generationNoun = statusItemCount === 1 ? "generation" : "generations";
  const statusText = `${mediaKindText} ${generationNoun}`;
  const promptDetail = generationJobDisplay?.promptDetails.join(", ") ?? "";

  return {
    summary: getToolDisplayText({
      completed:
        promptDetail.length > 0
          ? `Checked ${statusText} for ${promptDetail}`
          : `Checked ${statusText}`,
      pending:
        promptDetail.length > 0
          ? `Checking ${statusText} for ${promptDetail}`
          : `Checking ${statusText}`,
      running:
        promptDetail.length > 0
          ? `Checking ${statusText} for ${promptDetail}`
          : `Checking ${statusText}`,
    }),
    detail: null,
  };
}
